import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF as JsPdf } from 'jspdf';
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import './crm-contact-list.scss';
import DataGrid, {
  DataGridRef,
  Sorting, HeaderFilter, FilterRow, Scrolling, SearchPanel,
  Export, Column, Toolbar, Item, LoadPanel,
  Editing, DataGridTypes, StateStoring, RequiredRule, PatternRule, Lookup, Button as ColumnButton,
} from 'devextreme-react/data-grid';
import Popup from 'devextreme-react/popup';
import Form, { SimpleItem, GroupItem, ButtonItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridToXLSX } from 'devextreme/excel_exporter';
import { Relation } from '../../types/crm-contact';
import { ContactPanel } from '../../components';
import { useAuth } from '../../contexts/auth';
import DataSource from 'devextreme/data/data_source';
import { updateExactOnlineRelations, createRelation, updateRelation, deleteRelation, RelationFormData } from '../../api/relations';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
const onExporting = (e: DataGridTypes.ExportingEvent) => {
  if (e.format === 'pdf') {
    const doc = new JsPdf();
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: e.component,
    }).then(() => {
      doc.save('Contacts.pdf');
    });
  } else {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Contacts');

    exportDataGridToXLSX({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Contacts.xlsx');
      });
    });
    e.cancel = true;
  }
};

const exportFormats = ['xlsx', 'pdf'];

export const CRMContactList = () => {
  const [gridDataSource, setGridDataSource] = useState<DataSource<Relation[], string>>();
  const [isPanelOpened, setPanelOpened] = useState(false);
  const [contactId, setContactId] = useState<number>(0);
  const [relationPopupVisible, setRelationPopupVisible] = useState(false);
  const [editingOid, setEditingOid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [relationFormData, setRelationFormData] = useState<RelationFormData>({
    Name: '', Company: '', ContactPerson: '', CustomerNumber: '', Gender: 0,
    Email: '', InvoiceEmail: '', TelephoneNumber: '', Address: '', AddressNr: '', ZipCode: '', City: '',
  });
  const gridRef = useRef<DataGridRef>(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const store = new ODataStore({
      version: 4,
      key: 'Oid',
      keyType: 'Guid',
      url: `${process.env.REACT_APP_URL}/api/odata/Relation`,
      errorHandler: (error: any) => {
        if (error.httpStatus === 401) {
          signOut(true);
        }
      },
      beforeSend: (e: any) => {
        e.headers['Authorization'] = `Bearer ${user?.token}`;
        // OData v4 requires minimal metadata content type, not verbose
        e.headers['Accept'] = 'application/json;odata.metadata=minimal';
        if (e.method !== 'GET') {
          e.headers['Content-Type'] = 'application/json;odata.metadata=minimal';
        }
        if (e.params && e.params.$filter) {
          e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"=",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 eq $2)');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"<>",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 ne $2)');
        }
      },
    });

    const ds = new DataSource({
      store,
      select: [
        'Oid',
        'CustomerId',
        'CustomerNumber',
        'Name',
        'ContactPerson',
        'Email',
        'InvoiceEmail',
        'Gender',
        'TelephoneNumber',
        'Address',
        'AddressNr',
        'City',
        'ZipCode',
        'Company',
      ],
    });

    setGridDataSource(ds);
  }, [user?.token]);

  const changePanelOpened = useCallback(() => {
    setPanelOpened(!isPanelOpened);
    gridRef.current?.instance().option('focusedRowIndex', -1);
  }, [isPanelOpened]);

  const changePanelPinned = useCallback(() => {
    gridRef.current?.instance().updateDimensions();
  }, []);

  const onRowClick = useCallback(({ data }: DataGridTypes.RowClickEvent) => {
    setContactId(data.id);
    setPanelOpened(false);
    navigate('/crm-contact-details', { state: { relationOid: data.Oid._value.toString(), backTo: 'crmList' } });
  }, []);

  const refresh = useCallback(async()=> {
    try {
      await updateExactOnlineRelations(user?.token);
      // Success - refresh the grid
      gridRef.current?.instance().refresh();
      notify('Relaties succesvol gesynchroniseerd', 'success', 2000);
    } catch (error : any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 400 && typeof data === 'string' && data.toLowerCase().includes('no accounting provider')) {
        notify('Geen boekhoudprovider geconfigureerd voor deze tenant. Neem contact op met de beheerder.', 'warning', 4000);
      } else {
        notify(data || error.message || 'Synchronisatie mislukt', 'error', 3000);
      }
    }
  }, [user?.token]);

  const onAddRelationClick = useCallback(() => {
    setEditingOid(null);
    setRelationFormData({
      Name: '', Company: '', ContactPerson: '', CustomerNumber: '', Gender: 0,
      Email: '', InvoiceEmail: '', TelephoneNumber: '', Address: '', AddressNr: '', ZipCode: '', City: '',
    });
    setRelationPopupVisible(true);
  }, []);

  const onEditRelationClick = useCallback((row: Relation) => {
    const oid = (row as any).Oid?._value?.toString() ?? String(row.Oid);
    setEditingOid(oid);
    setRelationFormData({
      Name: row.Name ?? '',
      Company: row.Company ?? '',
      ContactPerson: row.ContactPerson ?? '',
      CustomerNumber: row.CustomerNumber ? String(row.CustomerNumber) : '',
      Gender: row.Gender ?? 0,
      Email: row.Email ?? '',
      InvoiceEmail: row.InvoiceEmail ?? '',
      TelephoneNumber: row.TelephoneNumber ?? '',
      Address: row.Address ?? '',
      AddressNr: row.AddressNr ?? '',
      ZipCode: row.ZipCode ?? '',
      City: row.City ?? '',
    });
    setRelationPopupVisible(true);
  }, []);

  const onRelationPopupSave = useCallback(async() => {
    if (!relationFormData.Name) {
      notify('Naam is verplicht', 'error', 2000);
      return;
    }
    setSaving(true);
    try {
      if (editingOid) {
        await updateRelation(editingOid, relationFormData, user?.token ?? '');
        notify('Relatie succesvol bijgewerkt', 'success', 2000);
      } else {
        await createRelation(relationFormData, user?.token ?? '');
        notify('Relatie succesvol aangemaakt', 'success', 2000);
      }
      setRelationPopupVisible(false);
      gridRef.current?.instance().refresh();
    } catch {
      notify('Er is een fout opgetreden bij het opslaan', 'error', 3000);
    } finally {
      setSaving(false);
    }
  }, [editingOid, relationFormData, user?.token]);

  const onClearFilters = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.instance().clearFilter();
      gridRef.current.instance().option('searchPanel.text', '');
    }
  }, []);

  const onRowRemoved = useCallback(async(e: any) => {
    try {
      const rowData = e.row?.data ?? e.data;
      const oid = rowData?.Oid?._value?.toString() ?? String(rowData?.Oid);
      await deleteRelation(oid, user?.token ?? '');
      notify('Relatie succesvol verwijderd', 'success', 2000);
      gridRef.current?.instance().refresh();
    } catch {
      notify('Fout bij verwijderen', 'error', 3000);
    }
  }, [user?.token]);

  const genderOptions = [
    { value: 0, text: 'Onbekend' },
    { value: 1, text: 'Man' },
    { value: 2, text: 'Vrouw' },
  ];

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid'
          noDataText=''
          focusedRowEnabled
          height='100%'
          dataSource={gridDataSource}
          onRowClick={onRowClick}
          onExporting={onExporting}
          onRowRemoved={onRowRemoved}
          allowColumnReordering
          ref={gridRef}>
          <LoadPanel showPane={false} />
          <Editing mode='row' />
          <SearchPanel width={240} visible placeholder='Contact Search' />
          <Export allowExportSelectedData formats={exportFormats} />
          <HeaderFilter visible allowSearch />
          <FilterRow visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <StateStoring
            enabled
            savingTimeout={500}
            type='localStorage'
            storageKey={`relationTableState_${user?.tenantName || 'all'}`}
          />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Relaties</div>
            </Item>
            <Item location='after' locateInMenu='auto'>
              <Button
                icon='plus'
                text='Relatie'
                type='default'
                stylingMode='contained'
                onClick={onAddRelationClick}
              />
            </Item>
            <Item
              location='after'
              locateInMenu='auto'
              showText='inMenu'
              widget='dxButton'
            >
              <Button
                icon='refresh'
                text='Refresh'
                stylingMode='text'
                onClick={refresh}
              />
            </Item>
            <Item
              location='after'
              locateInMenu='auto'
              showText='inMenu'
              widget='dxButton'>
              <Button
                icon='clearformat'
                text='Filters wissen'
                stylingMode='text'
                onClick={onClearFilters}
              />
            </Item>
            <Item location='after' locateInMenu='auto' />
            <Item name='exportButton' />
            <Item location='after' locateInMenu='auto' />
            <Item name='columnChooserButton' locateInMenu='auto' />
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>
          <Column dataField='CustomerNumber' alignment='left' width={90} caption='ID' />
          <Column sortOrder='asc' dataField='Name' caption='Naam'>
            <RequiredRule />
          </Column>
          <Column caption='Adres' allowEditing={false}
            calculateCellValue={(row: Record<string, string>) => `${row.Address || ''} ${row.AddressNr || ''}`.trim()} />
          <Column dataField='Address' caption='Straat' visible={false} />
          <Column dataField='AddressNr' caption='Huisnr' visible={false} />
          <Column dataField='City' caption='Plaats' />
          <Column dataField='ZipCode' caption='Postcode' visible={false} />
          <Column dataField='ContactPerson' caption='Contactpersoon' />
          <Column dataField='Email' caption='Email'>
            <PatternRule pattern={/^[^@\s]+@[^@\s]+\.[^@\s]+$/} message='Ongeldig e-mailadres' />
          </Column>
          <Column dataField='InvoiceEmail' caption='Factuur Email' visible={false}>
            <PatternRule pattern={/^[^@\s]+@[^@\s]+\.[^@\s]+$/} message='Ongeldig e-mailadres' />
          </Column>
          <Column dataField='TelephoneNumber' caption='Telefoon' />
          <Column dataField='Gender' caption='Geslacht' visible={false}>
            <Lookup dataSource={genderOptions} displayExpr='text' valueExpr='value' />
          </Column>
          <Column dataField='Company' caption='Bedrijf' visible={false} />
          <Column type='buttons' width={80}>
            <ColumnButton icon='trash' hint='Verwijderen' onClick={(e: any) => onRowRemoved(e)} />
            <ColumnButton
              icon='preferences'
              hint='Bewerken'
              onClick={(e: any) => onEditRelationClick(e.row?.data as Relation)}
            />
          </Column>
        </DataGrid>
        <Popup
          visible={relationPopupVisible}
          onHiding={() => setRelationPopupVisible(false)}
          title={editingOid ? 'Relatie bewerken' : 'Nieuwe relatie'}
          showTitle
          width={720}
          height={560}
          dragEnabled={false}
          showCloseButton
        >
          <Form
            formData={relationFormData}
            onFieldDataChanged={(e) => setRelationFormData(prev => ({ ...prev, [e.dataField as string]: e.value }))}
            colCount={2}
          >
            <GroupItem caption='Algemeen' colSpan={2} colCount={2}>
              <SimpleItem dataField='Name' label={{ text: 'Naam' }} isRequired />
              <SimpleItem dataField='Company' label={{ text: 'Bedrijf' }} />
              <SimpleItem dataField='ContactPerson' label={{ text: 'Contactpersoon' }} />
              <SimpleItem dataField='CustomerNumber' label={{ text: 'Klantnummer' }} />
              <SimpleItem dataField='Gender' label={{ text: 'Geslacht' }} editorType='dxSelectBox' editorOptions={{ items: genderOptions, displayExpr: 'text', valueExpr: 'value' }} />
            </GroupItem>
            <GroupItem caption='Contactgegevens' colSpan={2} colCount={2}>
              <SimpleItem dataField='Email' label={{ text: 'E-mail' }} />
              <SimpleItem dataField='InvoiceEmail' label={{ text: 'Factuur e-mail' }} />
              <SimpleItem dataField='TelephoneNumber' label={{ text: 'Telefoon' }} />
            </GroupItem>
            <GroupItem caption='Adres' colSpan={2} colCount={4}>
              <SimpleItem dataField='Address' colSpan={2} label={{ text: 'Straat' }} />
              <SimpleItem dataField='AddressNr' colSpan={1} label={{ text: 'Huisnr' }} />
              <SimpleItem dataField='ZipCode' colSpan={1} label={{ text: 'Postcode' }} />
              <SimpleItem dataField='City' colSpan={2} label={{ text: 'Plaats' }} />
            </GroupItem>
            <GroupItem colSpan={2} colCount={2} cssClass='form-buttons'>
              <ButtonItem buttonOptions={{ text: 'Annuleren', type: 'normal', stylingMode: 'outlined', onClick: () => setRelationPopupVisible(false) }} />
              <ButtonItem buttonOptions={{ text: editingOid ? 'Opslaan' : 'Aanmaken', type: 'default', stylingMode: 'contained', disabled: saving, onClick: onRelationPopupSave }} />
            </GroupItem>
          </Form>
        </Popup>
        <ContactPanel contactId={contactId} isOpened={isPanelOpened} changePanelOpened={changePanelOpened} changePanelPinned={changePanelPinned} />
      </div>
    </div>
  );
};
